import type { AppContext } from '@application/context/AppContext';
import { CabinetAccessService } from '@application/licence/CabinetAccessService';
import {
  CabinetPortfolioService,
  type PortfolioTenantSummary,
} from '@application/licence/CabinetPortfolioService';
import { listTenantUsers } from '@application/auth/UserDirectoryService';
import { userHasTenantAccess } from '@shared/access/tenantUserRole';
import type { Role, User } from '@shared/types/User';
import { esc } from '@presentation/shell/format';
import {
  CABINET_GRANTABLE_ROLES,
  cabinetGrantableRoleLabel,
  cabinetRoleForSelect,
  type CabinetGrantableRole,
} from './cabinetAccessLabels';

function cellIds(userId: string, tenantId: string): {
  checkId: string;
  roleId: string;
  errId: string;
  groupId: string;
} {
  const safeUser = userId.replace(/[^a-zA-Z0-9_-]/g, '_');
  const safeTenant = tenantId.replace(/[^a-zA-Z0-9_-]/g, '_');
  const base = `cam-${safeUser}-${safeTenant}`;
  return {
    checkId: `${base}-check`,
    roleId: `${base}-role`,
    errId: `${base}-err`,
    groupId: `${base}-group`,
  };
}

function isHomeTenant(user: User, tenantId: string): boolean {
  return user.tenant_id === tenantId;
}

function isLockedAccess(user: User, tenantId: string): boolean {
  if (isHomeTenant(user, tenantId)) return true;
  return user.accessible_tenant_ids === 'all';
}

function currentCellRole(user: User, tenantId: string): CabinetGrantableRole {
  const mapped = user.roles_by_tenant?.[tenantId];
  if (mapped) return cabinetRoleForSelect(mapped);
  if (isHomeTenant(user, tenantId) || userHasTenantAccess(user, tenantId)) {
    return cabinetRoleForSelect(user.role);
  }
  return 'lecture';
}

function roleOptionsHtml(selected: CabinetGrantableRole): string {
  return CABINET_GRANTABLE_ROLES.map(
    (role) =>
      `<option value="${esc(role)}"${role === selected ? ' selected' : ''}>${esc(cabinetGrantableRoleLabel(role))}</option>`,
  ).join('');
}

function renderCell(user: User, tenant: PortfolioTenantSummary): string {
  const hasAccess = userHasTenantAccess(user, tenant.id);
  const locked = isLockedAccess(user, tenant.id);
  const role = currentCellRole(user, tenant.id);
  const ids = cellIds(user.id, tenant.id);
  const userLabel = `${user.prenom} ${user.nom}`.trim() || user.email;
  const checkLabel = `Accès de ${userLabel} à ${tenant.nom}`;
  const roleLabel = `Rôle de ${userLabel} pour ${tenant.nom}`;
  const disabledAttr = locked ? ' disabled' : '';
  const checkedAttr = hasAccess || locked ? ' checked' : '';

  return `<td class="cabinet-access-cell">
    <div class="field" style="margin:0;min-width:10rem;" role="group" aria-labelledby="${esc(ids.groupId)}">
      <span id="${esc(ids.groupId)}" class="sr-only">${esc(checkLabel)}</span>
      <label for="${esc(ids.checkId)}" style="display:flex;align-items:center;gap:8px;margin:0 0 6px;font-size:12px;font-weight:600;color:var(--marine);cursor:pointer;">
        <input type="checkbox" id="${esc(ids.checkId)}" class="cabinet-access-check"
          data-user-id="${esc(user.id)}" data-tenant-id="${esc(tenant.id)}"
          ${checkedAttr}${disabledAttr}
          aria-describedby="${esc(ids.errId)}" />
        <span>Accès</span>
      </label>
      <label for="${esc(ids.roleId)}" class="sr-only">${esc(roleLabel)}</label>
      <select id="${esc(ids.roleId)}" class="cabinet-access-role" data-user-id="${esc(user.id)}" data-tenant-id="${esc(tenant.id)}"
        ${hasAccess || locked ? '' : ' disabled'}
        aria-invalid="false" aria-describedby="${esc(ids.errId)}">
        ${roleOptionsHtml(role)}
      </select>
      <p id="${esc(ids.errId)}" class="field-error" role="alert" hidden style="margin:6px 0 0;color:var(--rouge,#b42318);font-size:0.85rem;"></p>
    </div>
  </td>`;
}

function renderTable(users: User[], tenants: PortfolioTenantSummary[]): string {
  const head = tenants
    .map((t) => `<th scope="col">${esc(t.nom)}</th>`)
    .join('');
  const rows = users
    .map((user) => {
      const name = `${user.prenom} ${user.nom}`.trim() || user.email;
      return `<tr>
        <th scope="row">
          <div style="font-weight:700;color:var(--marine);">${esc(name)}</div>
          <div style="font-size:12px;color:var(--gris);">${esc(user.email)}</div>
        </th>
        ${tenants.map((t) => renderCell(user, t)).join('')}
      </tr>`;
    })
    .join('');

  return `
    <div class="card" style="margin-top:18px;">
      <div class="card-title">Accès par entreprise</div>
      <p style="margin:0 0 14px;font-size:13px;color:var(--gris);line-height:1.5;">
        Cochez une case pour autoriser un collaborateur sur une entreprise du portefeuille,
        puis choisissez son rôle pour cette entreprise. Par défaut, aucun accès n’est ouvert
        vers les autres entreprises.
      </p>
      <div class="table-scroll" style="overflow-x:auto;">
        <table class="data" aria-label="Matrice des accès utilisateurs par entreprise">
          <thead>
            <tr>
              <th scope="col">Utilisateur</th>
              ${head}
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    </div>`;
}

function showCellError(host: HTMLElement, userId: string, tenantId: string, message: string): void {
  const { errId, checkId, roleId } = cellIds(userId, tenantId);
  const err = host.querySelector<HTMLElement>(`#${CSS.escape(errId)}`);
  const check = host.querySelector<HTMLInputElement>(`#${CSS.escape(checkId)}`);
  const role = host.querySelector<HTMLSelectElement>(`#${CSS.escape(roleId)}`);
  if (err) {
    err.textContent = message;
    err.hidden = false;
  }
  check?.setAttribute('aria-invalid', 'true');
  role?.setAttribute('aria-invalid', 'true');
  (check ?? role)?.focus();
}

function clearCellError(host: HTMLElement, userId: string, tenantId: string): void {
  const { errId, checkId, roleId } = cellIds(userId, tenantId);
  const err = host.querySelector<HTMLElement>(`#${CSS.escape(errId)}`);
  const check = host.querySelector<HTMLInputElement>(`#${CSS.escape(checkId)}`);
  const role = host.querySelector<HTMLSelectElement>(`#${CSS.escape(roleId)}`);
  if (err) {
    err.textContent = '';
    err.hidden = true;
  }
  check?.setAttribute('aria-invalid', 'false');
  role?.setAttribute('aria-invalid', 'false');
}

function applyUserUpdate(users: User[], updated: User): void {
  const idx = users.findIndex((u) => u.id === updated.id);
  if (idx >= 0) users[idx] = updated;
}

function syncCellControls(
  host: HTMLElement,
  user: User,
  tenantId: string,
  hasAccess: boolean,
): void {
  const { checkId, roleId } = cellIds(user.id, tenantId);
  const check = host.querySelector<HTMLInputElement>(`#${CSS.escape(checkId)}`);
  const role = host.querySelector<HTMLSelectElement>(`#${CSS.escape(roleId)}`);
  const locked = isLockedAccess(user, tenantId);
  if (check) {
    check.checked = hasAccess || locked;
    check.disabled = locked;
  }
  if (role) {
    role.disabled = !(hasAccess || locked);
    role.value = currentCellRole(user, tenantId);
  }
}

async function grantAccess(
  ctx: AppContext,
  host: HTMLElement,
  users: User[],
  userId: string,
  tenantId: string,
  role: Role,
): Promise<boolean> {
  clearCellError(host, userId, tenantId);
  const result = await CabinetAccessService.grantTenantAccess(ctx, {
    targetUserId: userId,
    tenantId,
    role,
  });
  if (!result.ok) {
    showCellError(host, userId, tenantId, result.error);
    return false;
  }
  applyUserUpdate(users, result.user);
  syncCellControls(host, result.user, tenantId, true);
  return true;
}

async function revokeAccess(
  ctx: AppContext,
  host: HTMLElement,
  users: User[],
  userId: string,
  tenantId: string,
): Promise<boolean> {
  clearCellError(host, userId, tenantId);
  const result = await CabinetAccessService.revokeTenantAccess(ctx, {
    targetUserId: userId,
    tenantId,
  });
  if (!result.ok) {
    showCellError(host, userId, tenantId, result.error);
    return false;
  }
  applyUserUpdate(users, result.user);
  syncCellControls(host, result.user, tenantId, false);
  return true;
}

const UNEXPECTED_ERROR = 'Opération impossible pour le moment. Réessayez.';

/** Une erreur inattendue du service ne doit ni rester silencieuse ni laisser la case incohérente. */
async function safely(
  host: HTMLElement,
  userId: string,
  tenantId: string,
  run: () => Promise<boolean>,
): Promise<boolean> {
  try {
    return await run();
  } catch {
    showCellError(host, userId, tenantId, UNEXPECTED_ERROR);
    return false;
  }
}

const MATRIX_ABORT = new WeakMap<HTMLElement, AbortController>();

function bindMatrixEvents(
  host: HTMLElement,
  ctx: AppContext,
  users: User[],
): void {
  MATRIX_ABORT.get(host)?.abort();
  const ac = new AbortController();
  MATRIX_ABORT.set(host, ac);

  host.addEventListener(
    'change',
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLSelectElement)) return;

      const userId = target.dataset.userId ?? '';
      const tenantId = target.dataset.tenantId ?? '';
      if (!userId || !tenantId) return;

      const user = users.find((u) => u.id === userId);
      if (!user) return;

      if (target instanceof HTMLInputElement && target.classList.contains('cabinet-access-check')) {
        const { roleId } = cellIds(userId, tenantId);
        const roleSelect = host.querySelector<HTMLSelectElement>(`#${CSS.escape(roleId)}`);
        const role = cabinetRoleForSelect((roleSelect?.value as Role | undefined) ?? 'lecture');

        if (target.checked) {
          if (roleSelect) roleSelect.disabled = false;
          void safely(host, userId, tenantId, () =>
            grantAccess(ctx, host, users, userId, tenantId, role),
          ).then((ok) => {
            if (!ok) {
              target.checked = false;
              if (roleSelect) roleSelect.disabled = true;
            }
          });
          return;
        }

        void safely(host, userId, tenantId, () =>
          revokeAccess(ctx, host, users, userId, tenantId),
        ).then((ok) => {
          if (!ok) {
            target.checked = true;
            if (roleSelect) roleSelect.disabled = false;
          }
        });
        return;
      }

      if (target instanceof HTMLSelectElement && target.classList.contains('cabinet-access-role')) {
        const { checkId } = cellIds(userId, tenantId);
        const check = host.querySelector<HTMLInputElement>(`#${CSS.escape(checkId)}`);
        if (!check?.checked) return;
        const previous = currentCellRole(user, tenantId);
        const next = cabinetRoleForSelect(target.value as Role);
        void safely(host, userId, tenantId, () =>
          grantAccess(ctx, host, users, userId, tenantId, next),
        ).then((ok) => {
          if (!ok) target.value = previous;
        });
      }
    },
    { signal: ac.signal },
  );
}

/**
 * Affiche la matrice utilisateurs × entreprises (mode cabinet uniquement).
 * Le controller Utilisateurs a déjà exigé le rôle admin.
 */
export async function mountCabinetAccessMatrix(
  host: HTMLElement,
  ctx: AppContext,
): Promise<void> {
  host.replaceChildren();
  host.hidden = true;

  if (!CabinetPortfolioService.isCabinetMode(ctx)) {
    return;
  }

  const tenantId = ctx.tenantContext.tryGetTenant()?.id;
  const [tenants, users] = await Promise.all([
    CabinetPortfolioService.listSummaries(ctx),
    listTenantUsers(ctx, tenantId),
  ]);

  if (tenants.length === 0) {
    host.hidden = false;
    host.innerHTML = `
      <div class="card" style="margin-top:18px;">
        <div class="card-title">Accès par entreprise</div>
        <p style="margin:0;font-size:13px;color:var(--gris);">
          Aucune entreprise accessible dans le portefeuille pour le moment.
        </p>
      </div>`;
    return;
  }

  host.hidden = false;
  host.innerHTML = renderTable(users, tenants);

  if (!host.querySelector('style[data-cabinet-access-matrix]')) {
    const style = document.createElement('style');
    style.dataset.cabinetAccessMatrix = '1';
    style.textContent = `
      #cabinet-access-matrix .cabinet-access-check:focus-visible,
      #cabinet-access-matrix .cabinet-access-role:focus-visible {
        outline: 3px solid rgba(0,128,128,0.35);
        outline-offset: 2px;
      }
      #cabinet-access-matrix .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }
    `;
    host.appendChild(style);
  }

  bindMatrixEvents(host, ctx, users);
}
